import { useState, useEffect } from 'react';
import axios from 'axios';
import { useLanguage } from '../../contexts/LanguageContext';
import { 
    Rocket, 
    BookCheck, 
    GraduationCap, 
    Award, 
    Trophy, 
    Medal, 
    Crown, 
    Flame, 
    Star,
    CheckCircle2,
    Lock,
    ChevronRight
} from 'lucide-react';
import { cn } from '../../lib/utils';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const iconMap = {
    'rocket': Rocket,
    'book-check': BookCheck,
    'graduation-cap': GraduationCap,
    'award': Award,
    'trophy': Trophy,
    'medal': Medal,
    'crown': Crown,
    'flame': Flame,
    'star': Star
};

const colorMap = {
    'blue': { bg: 'bg-blue-50', text: 'text-blue-600', border: 'border-blue-200', fill: 'fill-blue-500' },
    'emerald': { bg: 'bg-emerald-50', text: 'text-emerald-600', border: 'border-emerald-200', fill: 'fill-emerald-500' },
    'purple': { bg: 'bg-purple-50', text: 'text-purple-600', border: 'border-purple-200', fill: 'fill-purple-500' },
    'amber': { bg: 'bg-amber-50', text: 'text-amber-600', border: 'border-amber-200', fill: 'fill-amber-500' },
    'orange': { bg: 'bg-orange-50', text: 'text-orange-600', border: 'border-orange-200', fill: 'fill-orange-500' },
    'red': { bg: 'bg-red-50', text: 'text-red-600', border: 'border-red-200', fill: 'fill-red-500' }
};

export const MilestoneTimeline = ({ maxItems = 6, showAll = false }) => {
    const { language } = useLanguage();
    const [milestones, setMilestones] = useState([]);
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        fetchMilestones();
    }, []);

    const fetchMilestones = async () => {
        try {
            const [milestonesRes, statsRes] = await Promise.all([
                axios.get(`${API}/milestones/available`),
                axios.get(`${API}/milestones/stats`)
            ]);
            setMilestones(milestonesRes.data);
            setStats(statsRes.data);
        } catch (error) {
            console.error('Error fetching milestones:', error);
        } finally {
            setLoading(false);
        }
    };

    if (loading) {
        return (
            <div className="glass-card rounded-2xl p-6 animate-pulse">
                <div className="h-6 bg-slate-200 rounded w-1/3 mb-4"></div>
                <div className="space-y-3">
                    {[1, 2, 3].map(i => (
                        <div key={i} className="h-16 bg-slate-100 rounded-xl"></div>
                    ))}
                </div>
            </div>
        );
    }

    const displayMilestones = showAll ? milestones : milestones.slice(0, maxItems);
    const achievedCount = milestones.filter(m => m.achieved).length;

    return (
        <div className="glass-card rounded-2xl overflow-hidden" data-testid="milestone-timeline">
            {/* Header */}
            <div className="p-6 border-b border-slate-100">
                <div className="flex items-center justify-between">
                    <div>
                        <h3 className="text-lg font-heading font-semibold text-slate-900">
                            {language === 'id' ? 'Perjalanan Milestone' : 'Milestone Journey'}
                        </h3>
                        <p className="text-sm text-slate-500 mt-1">
                            {achievedCount}/{milestones.length} {language === 'id' ? 'dicapai' : 'achieved'}
                        </p>
                    </div>
                    {/* Overall Progress Ring */}
                    <div className="relative w-14 h-14">
                        <svg className="w-full h-full -rotate-90">
                            <circle
                                cx="28"
                                cy="28"
                                r="24"
                                fill="none"
                                stroke="#e5e7eb"
                                strokeWidth="4"
                            />
                            <circle
                                cx="28"
                                cy="28"
                                r="24"
                                fill="none"
                                stroke="#C9182B"
                                strokeWidth="4"
                                strokeLinecap="round"
                                strokeDasharray={`${2 * Math.PI * 24}`}
                                strokeDashoffset={`${2 * Math.PI * 24 * (1 - (stats?.percentage || 0) / 100)}`}
                                className="transition-all duration-700"
                            />
                        </svg>
                        <span className="absolute inset-0 flex items-center justify-center text-xs font-bold text-slate-700">
                            {Math.round(stats?.percentage || 0)}%
                        </span>
                    </div>
                </div>
            </div>

            {/* Milestone List */}
            <div className="p-4 space-y-2 max-h-[400px] overflow-y-auto">
                {displayMilestones.map((milestone, index) => {
                    const Icon = iconMap[milestone.icon] || Award;
                    const colors = colorMap[milestone.color] || colorMap.blue;
                    const isAchieved = milestone.achieved;
                    const progress = milestone.target > 0 
                        ? Math.min((milestone.progress / milestone.target) * 100, 100) 
                        : 0;

                    return (
                        <div 
                            key={milestone.milestone_type}
                            className={cn(
                                "relative flex items-center gap-4 p-4 rounded-xl transition-all duration-300",
                                isAchieved 
                                    ? "bg-gradient-to-r from-primary/5 to-transparent border border-primary/10" 
                                    : "bg-slate-50/50 hover:bg-slate-50"
                            )}
                            data-testid={`milestone-${milestone.milestone_type}`}
                        >
                            {/* Icon */}
                            <div className={cn(
                                "w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 transition-all",
                                isAchieved 
                                    ? `${colors.bg} ${colors.text}` 
                                    : "bg-slate-100 text-slate-400"
                            )}>
                                {isAchieved ? (
                                    <Icon className="w-6 h-6" />
                                ) : (
                                    <Lock className="w-5 h-5" />
                                )}
                            </div>

                            {/* Content */}
                            <div className="flex-1 min-w-0">
                                <div className="flex items-center gap-2">
                                    <h4 className={cn(
                                        "font-medium truncate",
                                        isAchieved ? "text-slate-900" : "text-slate-500"
                                    )}>
                                        {language === 'en' && milestone.name_en ? milestone.name_en : milestone.name}
                                    </h4>
                                    {isAchieved && (
                                        <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                                    )}
                                </div>
                                <p className="text-xs text-slate-400 mt-0.5 truncate">
                                    {language === 'en' && milestone.description_en ? milestone.description_en : milestone.description}
                                </p>
                                
                                {/* Progress Bar (for non-achieved) */}
                                {!isAchieved && milestone.target > 0 && (
                                    <div className="mt-2 flex items-center gap-2">
                                        <div className="flex-1 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                                            <div 
                                                className="h-full bg-slate-400 rounded-full transition-all duration-500"
                                                style={{ width: `${progress}%` }}
                                            />
                                        </div>
                                        <span className="text-[10px] text-slate-400 font-medium">
                                            {milestone.progress}/{milestone.target}
                                        </span>
                                    </div>
                                )}
                            </div>

                            {/* Achieved Date */}
                            {isAchieved && milestone.achieved_at && (
                                <span className="text-[10px] text-slate-400 flex-shrink-0">
                                    {new Date(milestone.achieved_at).toLocaleDateString(language === 'id' ? 'id-ID' : 'en-US', {
                                        day: 'numeric',
                                        month: 'short'
                                    })}
                                </span>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* View All Link */}
            {!showAll && milestones.length > maxItems && (
                <div className="p-4 pt-0">
                    <button 
                        className="w-full py-3 text-sm text-primary font-medium hover:bg-primary/5 rounded-xl transition-colors flex items-center justify-center gap-1"
                        onClick={() => {/* Navigate to milestones page or expand */}}
                        data-testid="view-all-milestones"
                    >
                        {language === 'id' ? 'Lihat Semua Milestone' : 'View All Milestones'}
                        <ChevronRight className="w-4 h-4" />
                    </button>
                </div>
            )}
        </div>
    );
};
